import { styled } from '@porsche-code/kado-styled-system/jsx';
import { PButton, PButtonPure } from '@porsche-design-system/components-react';
import { useState } from 'react';

import { ActionGroup } from './ActionGroup';
import { Modal } from './Modal';
import { Spacer } from './Spacer';
import { SpinnerOverlay } from './Spinner';

const List = styled('ul', {
  base: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
});

const ListItem = styled('li', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: 'sm',
    cursor: 'pointer',
    margin: 0,
    padding: 'sm',
    borderRadius: 'xl',

    '&:hover': {
      backgroundColor: 'background.frostedSoft',
    },
  },
});

export interface CreateInput<T> {
  name: string;
  isFavorite?: boolean;
  filter: T;
}

export type PresetListProps<T> = {
  presets: {
    id: string;
    name: string;
    isFavorite?: boolean | null;
    filter: T;
  }[];
  selectedFilter: T;
  onFavor: (id: string, isFavorite: boolean) => void;
  onSelect: (preset: T) => void;
  onRemove: (id: string) => void;
  i18n: {
    delete: {
      confirmation: string;
      cancel: string;
      confirm: string;
    };
  };
  showSpinner?: boolean;
  children: React.ReactNode;
};

export const PresetList = <T,>(props: PresetListProps<T>) => {
  const { showSpinner, i18n, children, onFavor, onRemove, onSelect, presets } =
    props;
  const [deleteModal, setDeleteModal] = useState<{
    id?: string;
    isOpen: boolean;
  }>({
    isOpen: false,
  });

  return (
    <>
      {showSpinner && <SpinnerOverlay size="lg" />}
      <Modal
        open={deleteModal.isOpen}
        size="small"
        onDismiss={() => setDeleteModal({ isOpen: false })}
      >
        <Modal.Header>{i18n.delete.confirmation}</Modal.Header>

        <ActionGroup spacing="none">
          <PButton
            type="button"
            variant="secondary"
            onClick={(evt) => {
              evt.stopPropagation();
              setDeleteModal({
                isOpen: false,
              });
            }}
          >
            {i18n.delete.cancel}
          </PButton>
          <PButton
            type="button"
            variant="primary"
            onClick={(evt) => {
              evt.stopPropagation();
              if (deleteModal.id) {
                onRemove(deleteModal.id);
              }
              setDeleteModal({
                isOpen: false,
              });
            }}
          >
            {i18n.delete.confirm}
          </PButton>
        </ActionGroup>
      </Modal>
      <List>
        {presets
          .toSorted((preset) => (preset.isFavorite ? -1 : 1))
          .map((preset) => (
            <ListItem
              data-testid="filter-preset-list"
              key={preset.name}
              onClick={() => onSelect(preset.filter)}
            >
              <PButtonPure
                hideLabel
                onClick={(evt) => {
                  evt.stopPropagation();
                  onFavor(preset.id, !preset.isFavorite);
                }}
                icon={preset.isFavorite ? 'star-filled' : 'star'}
              />
              <div>{preset.name}</div>
              <PButtonPure
                hideLabel
                icon="delete"
                onClick={(evt) => {
                  evt.stopPropagation();
                  setDeleteModal({
                    id: preset.id,
                    isOpen: true,
                  });
                }}
              />
            </ListItem>
          ))}

        <Spacer mb="md" />

        {children}
      </List>
    </>
  );
};
